import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';
import { userService } from '../services/userService';
import { useLogger } from '../utils/logger';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const { currentUser, isAuthenticated } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const logger = useLogger('UserContext');

  useEffect(() => {
    const fetchUserData = async () => {
      if (!isAuthenticated || !currentUser) {
        setUserData(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get the ID token
        const idToken = currentUser.idToken;
        
        logger.debug('Current user available', { hasIdToken: !!idToken });
        
        if (!idToken) {
          logger.error('No ID token available');
          setLoading(false);
          return;
        }
        
        // Fetch user data from API with retries
        let retries = 5; // Increased from 3 to 5 retries
        let userData = null;
        let lastError = null;
        
        // Check if this is a new sign-up
        const isNewUser = sessionStorage.getItem('isNewUser') === 'true';
        const signupTimestamp = sessionStorage.getItem('signupTimestamp');
        const isRecentSignup = signupTimestamp && (Date.now() - parseInt(signupTimestamp)) < 60000; // Within 1 minute
        
        // For new users, add an initial delay before first attempt
        if (isNewUser && isRecentSignup) {
          logger.debug('New user detected, adding initial delay before first attempt');
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second initial delay
        }
        
        for (let i = 0; i < retries; i++) {
          try {
            logger.debug(`Attempt ${i+1} to fetch user data`);
            userData = await userService.getUserData(idToken);
            logger.debug('User data retrieved successfully');
            break; // Success, exit the retry loop
          } catch (err) {
            logger.error(`Attempt ${i+1} failed`, err);
            lastError = err;
            
            // Only retry for 404 errors (user not found)
            if (err.message && err.message.includes('404')) {
              if (i < retries - 1) {
                // Use a consistent 2-second delay between retries
                const delay = 2000; // 2 seconds for all retries
                logger.debug(`User data not found, retrying in ${delay}ms`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            } else {
              // For other errors, don't retry
              break;
            }
          }
        }
        
        if (userData) {
          setUserData(userData);
        } else if (lastError) {
          // Only set error if we've exhausted all retries
          logger.error('All attempts to fetch user data failed', lastError);
          setError(lastError);
        }
      } catch (err) {
        logger.error('Error fetching user data', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    
    // Listen for force refresh events
    const handleStorageChange = (e) => {
      if (e.key === 'forceRefresh') {
        logger.debug('Force refresh triggered');
        fetchUserData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentUser, isAuthenticated]);

  const value = {
    userData,
    loading,
    error,
    // Helper getters
    firstName: userData?.firstName || '',
    lastName: userData?.lastName || '',
    fullName: userData ? `${userData.firstName} ${userData.lastName}` : '',
    plan: userData?.plan || 'basic',
    email: userData?.email || '',
    createdAt: userData?.createdAt || ''
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};
